import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import "./ReminderPopup.css";
import { API_BASE } from "../apiConfig";

const ReminderPopup = () => {
  const [dueReminders, setDueReminders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const textRef = useRef(null);
  const gotItRef = useRef(null);
  const navigate = useNavigate();

  const SNOOZE_MS = 3 * 60 * 60 * 1000;

  const fetchAndShow = async (token) => {
    try {
      const response = await fetch(
        `${API_BASE}/therapist_api/get_today_reminders.php`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.status === "success" && data.count > 0) {
        setDueReminders(data.data);
        setCurrentIndex(0);
        setShowPopup(true);
        localStorage.setItem("reminderLastShownAt", String(Date.now()));
      }
    } catch (error) {
      console.error("Error fetching today's reminders:", error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    let cancelled = false;
    let snoozeTimer = null;

    const handleReminderCreated = () => {
      localStorage.removeItem("reminderSnoozedUntil");
      localStorage.removeItem("reminderLastShownAt");
      localStorage.removeItem("reminderDismissedAt");
      if (!cancelled) fetchAndShow(token);
    };

    window.addEventListener("reminder-created", handleReminderCreated);

    const prevToken = localStorage.getItem("reminderLastToken");
    const isFreshLogin = prevToken !== token;

    if (isFreshLogin) {
      localStorage.setItem("reminderLastToken", token);
      localStorage.removeItem("reminderSnoozedUntil");
      localStorage.removeItem("reminderLastShownAt");
      localStorage.removeItem("reminderDismissedAt");
      fetchAndShow(token);
      return () => { cancelled = true; window.removeEventListener("reminder-created", handleReminderCreated); };
    }

    const dismissedAt = Number(localStorage.getItem("reminderDismissedAt") || 0);
    if (dismissedAt) {
      return () => { cancelled = true; window.removeEventListener("reminder-created", handleReminderCreated); };
    }

    const snoozedUntil = Number(localStorage.getItem("reminderSnoozedUntil") || 0);
    const isSnoozed = snoozedUntil && Date.now() < snoozedUntil;

    if (isSnoozed) {
      snoozeTimer = setTimeout(() => {
        if (!cancelled) fetchAndShow(token);
      }, snoozedUntil - Date.now());
      return () => {
        cancelled = true;
        if (snoozeTimer) clearTimeout(snoozeTimer);
        window.removeEventListener("reminder-created", handleReminderCreated);
      };
    }

    const lastShownAt = Number(localStorage.getItem("reminderLastShownAt") || 0);
    if (lastShownAt && Date.now() - lastShownAt < SNOOZE_MS) {
      return () => { cancelled = true; window.removeEventListener("reminder-created", handleReminderCreated); };
    }

    fetchAndShow(token);

    return () => { cancelled = true; window.removeEventListener("reminder-created", handleReminderCreated); };
    // Runs once on mount: the token is read once, and SNOOZE_MS is a
    // module-level constant that cannot change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentReminder = dueReminders[currentIndex];
  const message = (currentReminder && currentReminder.reminder_message) || "";

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    setShowReadMore(el.scrollHeight > el.clientHeight + 1);
  }, [showPopup, currentIndex, message]);

  // Focus the "Ok, Got it" button when popup appears
  useEffect(() => {
    if (showPopup && gotItRef.current) {
      gotItRef.current.focus();
    }
  }, [showPopup, currentIndex]);

  const closeAll = () => {
    setShowPopup(false);
    setDueReminders([]);
    setCurrentIndex(0);
  };

  const showNext = () => {
    if (currentIndex + 1 < dueReminders.length) {
      setCurrentIndex(currentIndex + 1);
    } else {
      closeAll();
    }
  };

  const handleGotIt = async () => {
    if (!currentReminder || acknowledging) return;

    setAcknowledging(true);
    const token = localStorage.getItem("token");
    try {
      if (currentReminder.type === "goal") {
        await fetch(`${API_BASE}/therapist_api/bulk_delete.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ ids: [currentReminder.id] })
        });
      } else {
        await fetch(`${API_BASE}/therapist_api/ack_reminders.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ reminder_ids: [currentReminder.id] })
        });
      }
    } catch (error) {
      console.error("Error dismissing reminder:", error);
    }

    localStorage.removeItem("reminderSnoozedUntil");

    const isLast = currentIndex + 1 >= dueReminders.length;
    if (isLast) {
      localStorage.setItem("reminderDismissedAt", String(Date.now()));
    } else {
      localStorage.setItem("reminderLastShownAt", String(Date.now()));
    }

    setAcknowledging(false);
    showNext();
  };

  const handleRemindLater = () => {
    const snoozedUntil = Date.now() + SNOOZE_MS;
    localStorage.setItem("reminderSnoozedUntil", String(snoozedUntil));
    localStorage.setItem("reminderLastShownAt", String(Date.now()));
    closeAll();
  };

  const handleReadMore = () => {
    closeAll();
    navigate("/Reminder");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      closeAll();
    }
  };

  if (!showPopup || !currentReminder) return null;

  return (
    <div
      className="reminder-popup-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Reminder notification"
      onKeyDown={handleKeyDown}
    >
      <div className="reminder-popup-card">
        <div className="reminder-popup-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faBell} aria-hidden="true" />
        </div>
        <p ref={textRef} className="reminder-popup-text">
          {message}
        </p>
        {showReadMore && (
          <button className="reminder-popup-readmore" onClick={handleReadMore} type="button">
            Read more...
          </button>
        )}
        <div className="reminder-popup-actions">
          <button
            ref={gotItRef}
            className="reminder-popup-btn-gotit"
            onClick={handleGotIt}
            type="button"
          >
            Ok, Got it
          </button>
          <button className="reminder-popup-btn-later" onClick={handleRemindLater} type="button">
            Remind me later
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReminderPopup;

